import type { ComparisonPage } from '../types';

export const northbeamVsWarehouse: ComparisonPage = {
  slug: 'northbeam-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'northbeam',
  title: 'Northbeam vs a custom data warehouse: attribution or the full P&L?',
  metaTitle: 'Northbeam vs a Custom Data Warehouse (2026): Honest Comparison',
  description:
    'Northbeam is marketing measurement: a pixel, attribution, incrementality, and MMM. A warehouse is your business model. Most brands that need both run both.',
  keywords: ['northbeam alternative', 'northbeam vs', 'northbeam pricing', 'northbeam snowflake', 'ecommerce attribution'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Northbeam answers where your next ad dollar should go. A warehouse answers what the business earned. They overlap very little. If you spend heavily on paid media, Northbeam does things we do not build. If finance needs margin, inventory, and channel P&L, that is warehouse work, and Northbeam exports can feed it.',
  differences: [
    { heading: 'Measurement vs accounting', body: 'Northbeam models which ads caused orders using its pixel, attribution, incrementality tests, and MMM. A warehouse reconciles orders, refunds, fees, and costs.' },
    { heading: 'Ad-spend pricing vs project pricing', body: 'Northbeam prices by ad-spend band and pageviews, starting at $1,500 a month. A build is a project plus your own Snowflake and BI bills.' },
    { heading: 'Exports, not a shared warehouse', body: 'Northbeam raw data reaches your warehouse as file exports to S3 or Google Cloud Storage that you load. A build lives in your Snowflake.' },
  ],
  sections: [
    {
      heading: 'What Northbeam does that we do not',
      paragraphs: [
        'Its first-party pixel, several attribution models with view-through credit, automated incrementality testing, and optional MMM are a measurement stack that would take a data science team to build. Higher plans come with a media strategist who interprets the results. It documents setup at about two to four weeks for Shopify brands.',
      ],
    },
    {
      heading: 'Where a warehouse picks up',
      paragraphs: [
        'Northbeam’s Amazon integration reports revenue and ad spend, with attribution still in progress. TikTok Shop orders come through Shopify or its Orders API. Its profit view needs the product costs you supply, and we found no documented margin waterfall with shipping, payment fees, or returns, or custom formula metrics.',
        'That is the gap between [attributed and incremental CAC](/blog/blended-attributed-incremental-marginal-cac) and [contribution-margin LTV by channel](/blog/contribution-margin-ltv-by-acquisition-channel). The first is Northbeam’s job; the second needs a model of your costs.',
      ],
    },
  ],
  pickThem: [
    'You spend heavily on paid media and need attribution beyond ad-platform reporting.',
    'You want incrementality tests and MMM from one vendor.',
    'You want a media strategist interpreting the numbers.',
  ],
  pickUs: [
    'Your main need is margin, inventory, and channel P&L.',
    'Amazon, wholesale, or retail need to be modeled with Shopify.',
    'You need custom metrics and SQL on your own data.',
  ],
  byTeam: [
    { team: 'Paid-media-heavy DTC brand', pick: 'theirs', why: 'Pixel, attribution, and incrementality built for that spend.' },
    { team: 'CFO who needs channel contribution margin', pick: 'ours', why: 'Costs, fees, and refunds modeled the way finance reports them.' },
    { team: 'Growth brand with both needs', pick: 'both', why: 'Northbeam for media decisions; its exports load into the warehouse.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Northbeam?',
      answer: 'No. We do not build pixels, attribution models, or MMM. A warehouse replaces spreadsheet P&Ls and disagreeing reports; Northbeam replaces guesswork about ad performance.',
    },
    {
      question: 'Can Northbeam send data to Snowflake?',
      answer: 'Its documentation describes file exports to S3 or Google Cloud Storage that you load into your warehouse. Row-level touchpoint export is limited to some plans.',
    },
    {
      question: 'How much does Northbeam cost?',
      answer: 'Its pricing page shows Starter at $1,500 a month and Professional at $3,500 a month as starting estimates, with Growth and Enterprise quoted. Price depends on ad spend and pageviews.',
    },
  ],
  related: ['triple-whale-vs-custom-data-warehouse', 'klar-vs-custom-data-warehouse', 'polar-analytics-vs-custom-data-warehouse'],
};
